import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Below this width the side-by-side slide/participants split stops working:
 * the participants rail is 2 of 12 columns, so it collapses to ~100px and the
 * tiles turn into slivers with wrapped, clipped names.
 */
const NARROW_VIEWPORT_QUERY = '(max-width: 1023px)';

const matchesNarrow = () =>
  typeof window !== 'undefined' && window.matchMedia(NARROW_VIEWPORT_QUERY).matches;

const parseZoom = (raw: string | null) => {
  const value = parseFloat(raw || '1');
  return Number.isFinite(value) && value > 0 ? value : 1;
};

/**
 * Layout mode for the parent-dashboard embed.
 *
 * `isStacked` is deliberately gated on `source=parent`: the egress recorder always
 * renders this app at a fixed 2560x1440, and the tutor-manager view is desktop-only,
 * so neither can ever hit the stacked branch. That keeps the recorded layout
 * byte-identical while the parent iframe gets a portrait-friendly one.
 *
 * `zoom` mirrors the `?zoom=` CSS zoom applied on the recording root. Viewport
 * units resolve against the *unzoomed* viewport and are only then scaled by
 * zoom, so a raw `Nvh` inside that subtree ends up covering `N * zoom` of the
 * visible height. Anything sizing itself in vh has to divide by this value.
 */
export const useResponsiveLayout = () => {
  const [searchParams] = useSearchParams();
  const isParent = searchParams.get('source') === 'parent';
  const zoom = parseZoom(searchParams.get('zoom'));

  const [isNarrow, setIsNarrow] = useState(matchesNarrow);

  useEffect(() => {
    const mediaQuery = window.matchMedia(NARROW_VIEWPORT_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches);

    // Re-sync in case the width changed between first render and this effect.
    setIsNarrow(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);

    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return { isParent, isStacked: isParent && isNarrow, zoom };
};

export default useResponsiveLayout;
